import { ArrowDownward, ArrowUpward, ExpandLess, ExpandMore, KeyboardDoubleArrowUp } from '@mui/icons-material';
import { Button, ButtonGroup, Tooltip, Typography } from '@mui/material';
import React, { JSX } from 'react';
import { useSearchParams } from 'react-router';

interface PageNavProps {
	page: number;
	totalPages: number;
	containerRef: React.RefObject<HTMLDivElement | null>;
}

const PageNav: React.FC<PageNavProps> = ({ page, totalPages, containerRef }) => {
	//eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [_, setSearchParams] = useSearchParams();

	const pageRangeStart = page > 2 ? page - 3 : 0;
	const pageRangeEnd = page + 2 < totalPages ? page + 2 : totalPages;
	const onFirstPage = page === 1;
	const onLastPage = page === totalPages;

	const scrollToTop = () => {
		const chatElement = containerRef.current;

		if (chatElement) {
			chatElement.scrollTo(0, 0);
		}
	};

	const changePage = (newPage: number) => {
		setSearchParams({
			page: `${newPage}`,
		});
		scrollToTop();
	};

	const generatePageNav = (page: number) => {
		const links: JSX.Element[] = [];
		for (let i = pageRangeStart; i < pageRangeEnd; i++) {
			const isCurrent = i + 1 === page;
			links.push(
				<Button
					key={`page-nav-${i}`}
					onClick={() => changePage(i + 1)}
					sx={{
						textDecoration: isCurrent ? 'underline' : 'none',
						padding: 2,
						fontWeight: isCurrent ? 'bold' : 'normal',
					}}
					disabled={isCurrent}
					variant={isCurrent ? 'text' : 'contained'}
					data-testid={`page-nav-button-${i + 1}`}
				>
					<Typography variant="h6" color="inherit" component="p">
						{i + 1}
					</Typography>
				</Button>
			);
		}

		return <>{links.map((link) => link)}</>;
	};

	return (
		<ButtonGroup
			id="page-links-container"
			variant="contained"
			orientation="vertical"
			sx={{
				position: 'fixed',
				bottom: 80,
				right: 40,
				zIndex: 20,
				borderRadius: 2,
				'@media print': {
					display: 'none',
				},
			}}
		>
			<Tooltip title="Scroll to top" placement="left">
				<Button sx={{ padding: 2 }} onClick={scrollToTop} data-testid={'page-nav-button-top'}>
					<KeyboardDoubleArrowUp />
				</Button>
			</Tooltip>

			<Tooltip title={onFirstPage ? '' : 'Go to First Page'} placement="left">
				<Button disabled={onFirstPage} onClick={() => changePage(1)} sx={{ padding: 2, opacity: 1 }} data-testid={'page-nav-button-first'}>
					<ArrowUpward />
				</Button>
			</Tooltip>

			<Tooltip title={onFirstPage ? '' : 'Go to Previous Page'} placement="left">
				<Button sx={{ padding: 2 }} disabled={onFirstPage} onClick={() => changePage(page - 1)} data-testid={'page-nav-button-previous'}>
					<ExpandLess />
				</Button>
			</Tooltip>

			{generatePageNav(page)}

			{pageRangeEnd < totalPages && (
				<Tooltip title={`Total pages: ${totalPages}`} placement="left" data-testid={'page-nav-button-ellipsis'}>
					<Button sx={{ padding: 2 }} disableRipple>
						...
					</Button>
				</Tooltip>
			)}

			<Tooltip title={onLastPage ? '' : 'Go to Next Page'} placement="left">
				<Button sx={{ padding: 2 }} disabled={onLastPage} onClick={() => changePage(page + 1)} data-testid={'page-nav-button-next'}>
					<ExpandMore />
				</Button>
			</Tooltip>

			<Tooltip title={onLastPage ? '' : 'Go to Last Page'} placement="left">
				<Button sx={{ padding: 2 }} disabled={onLastPage} onClick={() => changePage(totalPages)} data-testid={'page-nav-button-last'}>
					<ArrowDownward />
				</Button>
			</Tooltip>
		</ButtonGroup>
	);
};

export default PageNav;
